import { Injectable } from "@angular/core";
import { of } from "rxjs";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { AdminService } from "../../services/admin.service";
import {
  BalanceWithdrawStatusChangeAttempt,
  BalanceWithdrawStatusChangeFailed,
  BalanceWithdrawStatusChangeSuccess,
  EAdminActions,
  FetchAuctionAttempt,
  FetchAuctionFailed,
  FetchAuctionsListAttempt,
  FetchAuctionsListFailed,
  FetchAuctionsListSuccess,
  FetchAuctionSuccess,
  FetchBalanceWithdrawListAttempt,
  FetchBalanceWithdrawListFailed,
  FetchBalanceWithdrawListSuccess,
  FetchTransactionsListAttempt,
  FetchTransactionsListFailed,
  FetchTransactionsListSuccess,
  FetchUnprocessedTransactionsListAttempt,
  FetchUnprocessedTransactionsListFailed,
  FetchUnprocessedTransactionsListSuccess,
  FetchUserDataAttempt,
  FetchUserDataFailed,
  FetchUserDataSuccess,
  RefundedPaymentAttempt,
  RefundedPaymentFailed,
  RefundedPaymentSuccess,
  RefundPaymentAttempt,
  RefundPaymentFailed,
  RefundPaymentSuccess,
  UpdateAuctionAttempt,
  UpdateAuctionFailed,
  UpdateAuctionSuccess,
} from "../actions/admin.action";
import { HttpErrorResponse } from "@angular/common/http";
import { switchMap, catchError, tap } from "rxjs/operators";
@Injectable()
export class AdminEffects {
  constructor(
    private actions$: Actions,
    private adminService: AdminService,
  ) {}

  @Effect()
  FetchAuctionsList$ = this.actions$.pipe(
    ofType<FetchAuctionsListAttempt>(EAdminActions.FetchAuctionsListAttempt),
    switchMap((action) =>
      this.adminService.fetchAuctionsList(action.payload).pipe(
        switchMap((response) => {
          return of(new FetchAuctionsListSuccess(response.data));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new FetchAuctionsListFailed([response.error]));
        }),
      ),
    ),
  );

  @Effect()
  FetchAuction$ = this.actions$.pipe(
    ofType<FetchAuctionAttempt>(EAdminActions.FetchAuctionAttempt),
    switchMap((action) =>
      this.adminService.fetchAuction(action.payload).pipe(
        switchMap((response) => {
          return of(new FetchAuctionSuccess(response));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new FetchAuctionFailed([response.error]));
        }),
      ),
    ),
  );

  @Effect()
  UpdateAuction$ = this.actions$.pipe(
    ofType<UpdateAuctionAttempt>(EAdminActions.UpdateAuctionAttempt),
    switchMap((action) =>
      this.adminService.updateAuction(action.payload, action.auctionId).pipe(
        switchMap((response) => {
          return of(new UpdateAuctionSuccess(response));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new UpdateAuctionFailed([response.error]));
        }),
      ),
    ),
  );

  @Effect()
  FetchBalanceWithdrawList$ = this.actions$.pipe(
    ofType<FetchBalanceWithdrawListAttempt>(
      EAdminActions.FetchBalanceWithdrawListAttempt,
    ),
    switchMap((action) =>
      this.adminService.fetchBalanceWithdrawList(action.payload).pipe(
        switchMap((response) => {
          return of(new FetchBalanceWithdrawListSuccess(response.data));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new FetchBalanceWithdrawListFailed([response.error]));
        }),
      ),
    ),
  );

  @Effect()
  FetchUnprocessedTransactionsList$ = this.actions$.pipe(
    ofType<FetchUnprocessedTransactionsListAttempt>(
      EAdminActions.FetchUnprocessedTransactionsListAttempt,
    ),
    switchMap((action) =>
      this.adminService.fetchUnprocessedTransactionsList(action.payload).pipe(
        switchMap((response) => {
          return of(new FetchUnprocessedTransactionsListSuccess(response.data));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(
            new FetchUnprocessedTransactionsListFailed([response.error]),
          );
        }),
      ),
    ),
  );

  @Effect()
  BalanceWithdrawStatusChange$ = this.actions$.pipe(
    ofType<BalanceWithdrawStatusChangeAttempt>(
      EAdminActions.BalanceWithdrawStatusChangeAttempt,
    ),
    switchMap((action) =>
      this.adminService
        .balanceWithdrawStatusChange(
          action.payload,
          action.balanceWithdrawItemId,
        )
        .pipe(
          switchMap((response) => {
            return of(new BalanceWithdrawStatusChangeSuccess(response.data));
          }),
          catchError((response: HttpErrorResponse) => {
            return of(new BalanceWithdrawStatusChangeFailed([response.error]));
          }),
        ),
    ),
  );

  @Effect()
  FetchUserData$ = this.actions$.pipe(
    ofType<FetchUserDataAttempt>(EAdminActions.FetchUserDataAttempt),
    switchMap((action) =>
      this.adminService.fetchUserData(action.payload).pipe(
        switchMap((response) => {
          return of(new FetchUserDataSuccess(response.data));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new FetchUserDataFailed([response.error]));
        }),
      ),
    ),
  );

  @Effect()
  FetchTransactionsList$ = this.actions$.pipe(
    ofType<FetchTransactionsListAttempt>(
      EAdminActions.FetchTransactionsListAttempt,
    ),
    switchMap((action) =>
      this.adminService.fetchTransactionsList(action.payload).pipe(
        switchMap((response) => {
          return of(new FetchTransactionsListSuccess(response.data));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new FetchTransactionsListFailed([response.error]));
        }),
      ),
    ),
  );

  @Effect()
  RefundPayment$ = this.actions$.pipe(
    ofType<RefundPaymentAttempt>(EAdminActions.RefundPaymentAttempt),
    switchMap((action) =>
      this.adminService.refundPayment(action.payload).pipe(
        switchMap((response) => {
          return of(new RefundPaymentSuccess(response.data)).pipe(
            tap(() => action.refreshData()),
          );
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new RefundPaymentFailed([response.error.errors]));
        }),
      ),
    ),
  );

  @Effect()
  RefundedPayment$ = this.actions$.pipe(
    ofType<RefundedPaymentAttempt>(EAdminActions.RefundedPaymentAttempt),
    switchMap((action) =>
      this.adminService.refundedPayment(action.payload).pipe(
        switchMap((response) => {
          return of(
            new RefundedPaymentSuccess({
              ...response.data,
              item_id: action.payload,
            }),
          ).pipe(tap(() => action.openRefundedModalState()));
        }),
        catchError((response: HttpErrorResponse) => {
          return of(new RefundedPaymentFailed([response.error])).pipe(
            tap(() => action.openRefundedModalState()),
          );
        }),
      ),
    ),
  );
}
